import { ITrimEditorManagerBase } from '../trim-editor.manager';
import { FreeTextType } from '../../../models/api/front/layout';
import { DispatchAction, SelectorAction } from './trim-data-actions';
import { TrimCjContainer } from '../../model/cj-factory';
import {
  createJsonParam,
  FocusModeType,
  FrameContainerType,
  OrderFrameGroupType,
  OrderPictureGroupType,
  StorePageDataType,
  StoreTemplateType,
  TrimCompFilesType,
} from './trim-data-type';
import { TrimCjTool } from '../../../utilities/trim.cj-tool';
import { CJ_CONTAINER_NAME, CJ_DPOBJ_NAME } from '../../model/cj-factory/trim.cj-obj-name.collection';
import { TrimPrintManager, TrimPrint } from '../../print/trim.print';
import { TrimUiManager } from '../ui/trim.ui.manager';
import { TextImageData, TrimDataFormatter } from './trim-data.formatter';
import { TrimType } from '../trimming/trim.trimming.manager';
import { store } from '../../../app/store';
import { TrimEditableImageManager } from '../editable-image/trim.editable-image.manager';
import { dialogActions } from '../../../components/dialog/slice/dialog-slice';
import { XmlStructureModel } from '../../../xml/model/xml-structure-model';
import { apiActions } from '../../../slices/api-slice';
import { TrimThumbManager } from '../thumb/trim.thumb.manager';
import { cloneDeep, reject } from 'lodash';
import { TrimTextImageManager } from '../text-image/trim.text-image.manager';
import { PreviewType } from '../../../components/pages/layout/image-preview/layout.image-preview';
import { TrimImageEditManager } from '../image-edit/trim.image-edit.manager';
import { LayoutCompFileType } from '../../../layout-editor/manager/data/layout-data-type';
import { UuidGenerator } from '../../../utilities/uuid-generator';
import {
  PrintDataStructure,
  PrintItemDataEntity,
  PrintProductImageDataEntity,
} from '../../../models/sift/json/print-model';
import { ApiMetaOrderPost } from '../../../api/front/meta/api-meta-order';
import { printActions } from '../../../slices/sift/print-slice';
import { DATE_TEXT_ID, PrintTrimData } from '../../model/trim.print-model';
import { ApiPrintShopOrderDataPost } from '../../../api/sift/front/api-print-meta-shop-order-data';
import { ResponseBase } from '../../../api/response-base';
import { resolve } from 'path';
import { ApiPrintImagePost, ApiPrintImagesDelete, ApiPrintImagesGet } from '../../../api/sift/front/api-print-image';
import { IMAGES_KIND } from '../../../layout-editor/manager/image-processor/image-processor.manager';
import { ImagesGetResponse } from '../../../api/front/images/api-images';
import { TrimLaboOrderFactory } from '../../factory/labo-order/trim.labo-order.factory';
import { sleep } from '../../../utilities/sleep';

export type TrimListParam = {
  id: number,
  data: TrimType
}
type Nullable<T> = T | null;

export class TrimDataManager implements ITrimEditorManagerBase {

  private uiManager!: TrimUiManager;
  private printManager!: TrimPrintManager;
  private editableImageManager!: TrimEditableImageManager;
  private imageEditManager!: TrimImageEditManager;
  private previewManager!: TrimThumbManager;
  private textManager!: TrimTextImageManager;

  private editorContainer: Nullable<TrimCjContainer> = null;
  private dateTextContainer: Nullable<TrimCjContainer> = null;
  private textContainers: TrimCjContainer[] = [];
  private freeGraphics: TrimCjContainer[];

  // オーダー用
  private orderPages: StorePageDataType[];
  private templates: StoreTemplateType[];
  private frameGroups: OrderFrameGroupType[];
  private pictureGroups: OrderPictureGroupType[];
  private freeText: FreeTextType[];

  private trimList: TrimListParam[] = [];

  private guideMode: 'on' | 'off';
  private focusMode: FocusModeType = 'scale';
  private fitNum: number = 5;

  // 台紙モードかどうか
  private currentPrint: TrimPrint | null = null;
  // - テンプレートが大きすぎるのでこの値で割って調整 -
  private static ADDITIONAL_VALUE = 2;

  constructor() {
    this.orderPages = [];
    this.templates = [];
    this.frameGroups = [];
    this.pictureGroups = [];
    this.freeGraphics = [];
    this.freeText = [];
    this.guideMode = 'on';
  }

  initialize(): void {

    this.uiManager.on('r->t:complete-trim', async (v) => {
      try {
        await this.saveTrimData(v);
      } catch (e) {
        console.error(e);
        this.uiManager.emit('t->r:wait-loading', { message: '' });
        store.dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            `レイアウト情報の保存に失敗しました。`,
            `再度、「レイアウト完了」ボタンをクリックしてください。`,
          ],
          buttons: [
            {
              label: 'OK',
              callback: () => {
                store.dispatch(dialogActions.pop());
              },
            },
          ],
        }));
      }
    });
    this.uiManager.on('r->t:save-trim', async (v) => {
      try {
        await this.saveTrimData(v);
      } catch (e) {
        console.error(e);
        this.uiManager.emit('t->r:wait-loading', { message: '' });
        store.dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            `レイアウト情報の保存に失敗しました。`,
            `再度、「一時保存」ボタンをクリックしてください。`,
          ],
          buttons: [
            {
              label: 'OK',
              callback: () => {
                store.dispatch(dialogActions.pop());
              },
            },
          ],
        }));
      }
    });
    // this.uiManager.on('t->r:getOnSave', (v) => {
    //   v.callback(this.isOnSave);
    // });
    // this.uiManager.on('r->t:change-cover:start', () => {
    //   const _prevCovers = this.editorContainers.filter(v => {
    //     return store.getState().layout.pageTypeList.cover.find(cover => cover.pageTypeID === v.pageType);
    //     // return COVER_LIST.find(cover => cover.name === v.pageType);
    //   });
    //   const coverFreeGraphics = this.freeGraphics.filter(v => {
    //     return v.pageId === _prevCovers[0].pageId;
    //   });
    //   const coverText = this.textContainers.filter(v => {
    //     return v.pageId === _prevCovers[0].pageId;
    //   });
    //   this.prevCovers = _prevCovers.map((v) => cloneDeep(v));
    //   this.coverFreeGraphics = coverFreeGraphics.map((v) => cloneDeep(v));
    //   this.coverText = coverText.map((v) => cloneDeep(v));
    //   if (_prevCovers.length) {
    //     for (const cover of _prevCovers) {
    //       this.editorContainers = this.editorContainers.filter(v => v.pageId !== cover.pageId);
    //       this.imageAreaContainers = this.imageAreaContainers.filter(v => v.pageId !== cover.pageId);
    //       this.scaleContainers = this.scaleContainers.filter(v => v.pageId !== cover.pageId);
    //       this.rotateContainers = this.rotateContainers.filter(v => v.pageId !== cover.pageId);
    //       this.textContainers = this.textContainers.filter(v => v.pageId !== cover.pageId);
    //       this.freeGraphics = this.freeGraphics.filter(v => v.pageId !== cover.pageId);
    //       if (TrimCjTool.checkPhotoInFrame(cover)) {
    //         const photo = TrimCjTool.getImageContainer(cover);
    //         this.imageContainers = this.imageContainers.filter(v => v.editableImageId !== photo.editableImageId);
    //       }
    //       ;
    //     }
    //     ;
    //   }
    // });
    // this.uiManager.on('r->t:change-cover:end', () => {
    //   this.prevCovers.length = 0;
    // });
  }

  destroy(): void {
    this.orderPages.length = 0;
    this.templates.length = 0;
    this.frameGroups.length = 0;
    this.pictureGroups.length = 0;
    this.freeGraphics.length = 0;
    this.freeText.length = 0;
    this.fitNum = 5;
    this.guideMode = 'on';
    this.textContainers = [];
    this.editorContainer = null;
    this.uiManager.off('r->t:save-trim');
    this.uiManager.off('r->t:complete-trim');
    // this.uiManager.off('t->r:getOnSave');
    // this.uiManager.off('r->t:change-cover:start');
    // this.uiManager.off('r->t:change-cover:end');
  }

  di(
    uiMng: TrimUiManager,
    printMng: TrimPrintManager,
    editableMng: TrimEditableImageManager,
    imgEditMng: TrimImageEditManager,
    previewMng: TrimThumbManager,
    textMng: TrimTextImageManager,
  ): void {
    this.uiManager = uiMng;
    this.printManager = printMng;
    this.editableImageManager = editableMng;
    this.imageEditManager = imgEditMng;
    this.previewManager = previewMng;
    this.textManager = textMng;
  }

  private async urlToFile(url: string, id: string) {
    return new Promise<File>((resolve) => {
      fetch(url)
        .then(response => response.blob())
        .then(blob => new File([blob], `${id}.jpg`, { type: 'image/jpg' }))
        .then(file => {
          resolve(file);
        });
    });
  }
  // private async formatPreview(list: PreviewType[]) {
  //   return new Promise<LayoutCompFileType[]>(async (resolve) => {
  //     const files: LayoutCompFileType[] = [];
  //     for (const preview of list) {
  //       files.push({
  //         file: await this.urlToFile(preview.url),
  //         album: preview.album,
  //       });
  //     }
  //     resolve(files);
  //   });
  // }

  private async createThumb(stage: TrimCjContainer, kijshopCd: string, shopOrderId: string, orderId: string, imageId: string, isPreview: boolean = false, retryCount: number = 0): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      if (retryCount > 3) {
        reject('clash thumb !!!!!!');
        return;
      }
      this.uiManager.emit('t->r:wait-loading', { message: 'サムネイル画像を生成しています...' });
      const url = this.previewManager.makeThumbInCanvas(this.imageEditManager.getCanvasEle(), stage, this.printManager);
      const file = await this.urlToFile(url, imageId + (isPreview ? '' : DATE_TEXT_ID));
      window.setTimeout(() => {
        store.dispatch(apiActions.run( new ApiPrintImagePost({
          id: isPreview ? imageId : ( imageId + DATE_TEXT_ID),
          kind: IMAGES_KIND.THUMB_PREVIEW,
          // kind: isPreview ? IMAGES_KIND.THUMB_PREVIEW : IMAGES_KIND.TMP_THUMB,
          data: file,
          kijshopCd,
          shopOrderId,
          filename: file.name,
          exifCreated: '',
        }), {
          onSuccess: () => resolve(),
          onError: () => this.createThumb(stage, kijshopCd, shopOrderId, orderId, imageId, isPreview, retryCount + 1).catch((e) => reject(e)),
        }))
      }, 500);
    });
  }

  private saveFile(param: {
    file: File,
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
  }): Promise<void> {
    const { file, kijshopCd, shopOrderId, orderId } = param;
    return new Promise<void>((resolve) => {
      this.uiManager.emit('r->t:add-layout-comp', {
        file,
        kijshopCd,
        shopOrderId,
        orderId,
        callback: () => resolve(),
        onError: () => {
          resolve();
        },
      });
    });
  }

  private checkStructureUpdate(orderId: string, imageId: string) {
    const defaultStructure = this.printManager.defaultStructure;
    const structure = this.printManager.structure;
    const defaultItem = defaultStructure?.item?.[orderId];
    const item = structure?.item?.[orderId];
    if (!defaultItem || !item) {
      throw new Error('not found itemModel !!!')
    }
    const defaultImage = defaultItem.images.find(v => v.id === this.printManager.editableImageId);
    const image = item.images.find(v => v.id === this.printManager.editableImageId);
    if (!defaultImage || !image) {
      throw new Error('not found image !!!')
    }
    return image.thumbId !== imageId || image.rotate !== defaultImage.rotate;
  }
  private updateStructure(structure: PrintDataStructure, param: createJsonParam) {
    return new Promise<void>(async (resolve, reject) => {
      const { kijshopCd, shopOrderId, orderId } = param;
      store.dispatch(printActions.shopOrder({kijshopCd, shopOrderId}).update(structure, {
        success: () => resolve(),
      }));
    })
  }

  // NOTE: Debug用
  private debugExportJson(data: string, fileName: string) {
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName; // 保存されるファイル名
    a.click();

    URL.revokeObjectURL(url);
  }

  private async savePrintTrimData(data: PrintTrimData, param: createJsonParam) {
    const { kijshopCd, shopOrderId, orderId } = param;
    return new Promise<void>(async (resolve, reject) => {
      const resultFileData = data.exportJson();

      // this.debugExportJson(resultFileData.body, resultFileData.filename);

      store.dispatch(apiActions.run(
        new ApiPrintShopOrderDataPost({
          kijshopCd,
          shopOrderId,
          files: [
            {
              ...resultFileData,
              is_create: this.printManager.isUpdate ? '0' : '1',
            },
          ],
        }),
        {
          onSuccess: (res: ResponseBase<{}>) => {
            if (Number(res.error.errorCode) !== 200)  {
              reject(res.error.errorDetail);
            } else {
              resolve();
            }
          },
          onError: () => reject(),
        },
        {
          ignoreSystemError: true,
        }
      ));
    })
  }


  private async saveTrimData(param: createJsonParam) {
    const { kijshopCd, shopOrderId, orderId } = param;
    if (!this.printManager.currentPrint || !this.printManager.structure || !this.printManager.printTrimData || !this.imageEditManager.stage) {
      throw new Error('not found printModel !!!')
    }
    const structure = this.printManager.structure;
    const item = structure.item?.[orderId];
    if (!item) {
      throw new Error('not found itemModel !!!')
    }
    const image = item.images.find(v => v.id === this.printManager.editableImageId);
    if (!image) {
      throw new Error('not found image !!!')
    }
    const imageId = image.thumbId ? image.thumbId : UuidGenerator.create();
    const stage = cloneDeep(this.imageEditManager.stage)
    const dateText = TrimCjTool.getDateTextContainer(stage);
    // const dateText = TrimCjTool.getDateTextContainer(this.imageEditManager.stage);
    const isDate = !!cloneDeep(dateText?.visible);
    const showAbleArea = this.imageEditManager.printMenu.modeAbleArea === 'off';
    if (dateText) dateText.visible = false;
    if (showAbleArea) TrimUiManager.ins.emit('r->t:image-guide-on/off', { mode:  'on' });
    await sleep(1)
    try {
      await this.createThumb(stage, param.kijshopCd, param.shopOrderId, param.orderId, imageId, true);
      if (dateText) dateText.visible = true;
      await sleep(1)
      if (dateText) dateText.visible = isDate;
      await this.createThumb(stage, param.kijshopCd, param.shopOrderId, param.orderId, imageId);
      if (showAbleArea) TrimUiManager.ins.emit('r->t:image-guide-on/off', { mode:  'off' });
    } catch (e) {
      console.error(e);
      if (dateText) dateText.visible = isDate;
      throw new Error('create thumb rejected !!!!!!')
    }
    this.uiManager.emit('t->r:wait-loading', { message: 'トリミング情報を保存しています...' });
    const editableImage = this.printManager.editableImage;
    if (!editableImage) {
      throw new Error('not found editableImage !!!!!!')
    }

    // // Note ラボ発注モジュール検証用
    const factory = new TrimLaboOrderFactory();
    const json = await factory.createTrimJson({
      kijshopCd,
      shopOrderId,
      orderId,
      image: {
        id: editableImage.id,
        width: Number(editableImage.realWidth),
        height: Number(editableImage.realHeight),
        exif: editableImage.exif,
      },
      structure: cloneDeep(structure),
    })

    // this.debugExportJson(json.body, json.filename);

    const trimDataFormatter = new TrimDataFormatter(cloneDeep(this.printManager.printTrimData), this.printManager, this.editableImageManager, this.imageEditManager, this);
    const printTrimData = trimDataFormatter.canvas2LaboOrder();
    // console.log('x: ', printTrimData.image.x);
    // console.log('y: ', printTrimData.image.y);
    // console.log('w: ', printTrimData.image.width);
    // console.log('h: ', printTrimData.image.height);
    // console.log(`<object rotation="${printTrimData.image.rotate}" x="${printTrimData.image.x}" y="${printTrimData.image.y}" width="${printTrimData.image.width}" height="${printTrimData.image.height}" version="2">`);
    // console.log(`<window rotation="${printTrimData.texts?.[0]?.rotate}" x="${printTrimData.texts?.[0].x}" y="${printTrimData.texts?.[0].y}" width="${printTrimData.texts?.[0].width}" height="${printTrimData.texts?.[0].height}" attribute="24">`);
    // if (printTrimData.texts.length) {
    //   console.log(`<object type="string">`);
    //   console.log(`<edge width="${printTrimData.texts?.[0].style.outline.width}" color="${printTrimData.texts?.[0].style.outline.color}">`);
    //   console.log(`<antialias enable="1">`);
    //   console.log(`<string point="${printTrimData.texts?.[0].point}" color="${printTrimData.texts?.[0].style.color}" form="0" face="${printTrimData.texts?.[0].font}">`);
    //   console.log(`<text>${printTrimData.texts?.[0].value}</text>`);
    // }

    await this.deletePngImages(printTrimData);
    try {
      await this.savePrintTrimData(printTrimData, param)
      TrimUiManager.ins.emit('t->r:wait-loading', { message: '' });
      if (this.checkStructureUpdate(orderId, imageId)) {
        image.thumbId = imageId;
      }
        image.trimmingStatus = 2;
        await this.updateStructure(structure, param);
      param.callback();
    } catch (e) {
      console.error(e);
      TrimUiManager.ins.emit('t->r:wait-loading', { message: '' });
      throw new Error('trim save rejected !!!!!!')
    }
  }

  private deletePngImages(printData: PrintTrimData): Promise<void> {
    return new Promise(async (resolve) => {
      const kijshopCd = this.printManager.kijshopCd;
      const shopOrderId = this.printManager.shopOrderId;
      const stamps = printData?.stamps ?? [];
      const pngList = await new Promise<ImagesGetResponse[]>((resolve) => {
        store.dispatch(apiActions.run(
          new ApiPrintImagesGet({
            kijshopCd,
            shopOrderId,
            kind: IMAGES_KIND.FREE_GRAPHICS,
          }),
          {
            onSuccess: (v: any) => {
              resolve(v.body.data?.fileList ?? []);
            },
            onError: () => resolve([]),
          },
        ));
      });
      const pathList = pngList.filter((v) => !stamps.find(stamp => stamp.fileName === v.filename))
        .map((v) => v.filename);
      await Promise.all(pathList.map((v) => new Promise<void>((_resolve) => {
        store.dispatch(apiActions.run(
          new ApiPrintImagesDelete({
            kijshopCd,
            shopOrderId,
            kind: IMAGES_KIND.FREE_GRAPHICS,
            filename: v,
          }),
          {
            onSuccess: () => _resolve(),
            onError: () => _resolve(),
          },
          {
            ignoreSystemError: true,
          },
        ));
      })));
      console.log(pathList);
      resolve();
    });
  };

  private imageDeleteAsync = (path: string, shopData: {
    kijshopCd: string,
    shopOrderId: string
  }) => new Promise<void>((resolve) => {
    const _path = path.replace('Except_Real/', '');
    store.dispatch(apiActions.run(
      new ApiPrintImagesDelete({
        kijshopCd: shopData.kijshopCd,
        shopOrderId: shopData.shopOrderId,
        kind: '4',
        filename: _path,
      }),
      {
        onSuccess: () => resolve(),
      },
    ));
  });

  // private imageSaveAsync = (file: File, shopData: {
  //   kijshopCd: string,
  //   shopOrderId: string
  // }) => new Promise<void>((resolve) => {
  //   store.dispatch(apiActions.run(
  //     new ApiPrintImagesPost({
  //       kind: '4',
  //       data: file,
  //       kijshopCd: shopData.kijshopCd,
  //       shopOrderId: shopData.shopOrderId,
  //       filename: file.name,
  //     }),
  //     { onSuccess: () => resolve() },
  //   ));
  // });
  // private saveAndDeleteTextImage = async (textData: TextImageData, shopData: {
  //   kijshopCd: string,
  //   shopOrderId: string
  // }) => {
  //   await Promise.all(textData.deleteData.map((v) => this.imageDeleteAsync(v, shopData)));
  //   await Promise.all(textData.textData.map((v) => this.imageSaveAsync(v, shopData)));
  // };

  static get getAdditionalValue(): number {
    return TrimDataManager.ADDITIONAL_VALUE;
  }

  static set setAdditionalValue(val: number) {
    const BEST_SIZE = 50;
    TrimDataManager.ADDITIONAL_VALUE = Math.round(val / BEST_SIZE);
  }

  private throwIfNull<T>(value: Nullable<T>, name: string): T {
    if (value === null) {
      throw new Error(`${name} is not initialized`);
    }
    return value;
  }

  // - setter -
  setEditor(editor: TrimCjContainer) {
    this.editorContainer = editor;
  }
  setFreeGraphic(freeGraphics: TrimCjContainer) {
    this.freeGraphics = [...this.freeGraphics, freeGraphics];
  }
  removeFreeGraphic(id: string) {
    this.freeGraphics = this.freeGraphics.filter(v => v.editableImageId !== id);
  }
  changeGuideMode(mode: 'on' | 'off') {
    this.guideMode = mode;
  }
  setText(text: TrimCjContainer) {
    this.textContainers = this.textContainers.filter(v => v.textId !== text.textId);
    this.textContainers = [...this.textContainers, text];
  }
  removeText(id: string) {
    this.textContainers = this.textContainers.filter(v => v.textId !== id);
  }
  setFocusMode(mode: FocusModeType) {
    this.focusMode = mode;
  }
  setCurrentPrint(print: TrimPrint) {
    this.currentPrint = print;
  }
  setFitNum(fitNum: number) {
    this.fitNum = fitNum;
  }
  setTrimData(trimData: TrimListParam) {
    const { id, data } = trimData;
    if (this.trimList.length) {
      for (const v of this.trimList) {
        if (Boolean(this.trimList.find(v => v.id === id))) {
          if (v.id === id) {
            this.trimList = this.trimList.filter(v2 => v2.id !== id);
            this.trimList = [...this.trimList, trimData];
            this.trimList.sort((a, b) => a.id - b.id);
          }
        } else {
          this.trimList = [...this.trimList, trimData];
        }
      }
    } else {
      this.trimList.push(trimData);
    }
  }
  // - getter -
  getEditor(): TrimCjContainer {
    return this.throwIfNull(this.editorContainer, 'editorContainer');
  }
  getImageArea(): TrimCjContainer {
    return TrimCjTool.getImageAreaContainer(this.getEditor());
  }
  getScaleContainer(): TrimCjContainer {
    return TrimCjTool.getScaleContainer(this.getEditor());
  }
  getRotateContainer(): TrimCjContainer {
    return TrimCjTool.getRotateContainer(this.getEditor());
  }
  getFreeGraphics(): TrimCjContainer[] {
    return this.freeGraphics;
  }
  getTexts(): TrimCjContainer[] {
    return this.textContainers;
  }
  getText(id: string): TrimCjContainer | undefined {
    return this.textContainers.find(v => v.textId === id);
  }
  getFocusMode(): FocusModeType {
    return this.focusMode;
  }
  getCurrentPrint(): TrimPrint {
    return this.throwIfNull(this.currentPrint, 'currentPrint');
  }
  getImage(): TrimCjContainer {
    return TrimCjTool.getImageContainer(this.getEditor());
  }
  getPhotoContainer(): TrimCjContainer {
    return TrimCjTool.getPhotoContainer(this.getEditor());
  }
  getFitNum(): number {
    return this.fitNum;
  }

  // data.dispatchをラップ
  // async dispatch(action: DispatchAction) {
  // }
  //
  // selector(action: SelectorAction) {
  //
  // }

  // getAdditionalWindow(photoAreaId: string, pageId: string) {
  //   if (this.selector('getDAIFlag')) {
  //     const targetWindow = this.windows.find((v) => v.pageId === pageId);
  //     // const photoAreaData = this.albumManager.currentPrint?.parseData.templateData.photoarea;
  //     // const targetPa = photoAreaData.find((elm: any) => elm.$.photoareaID === photoAreaId);
  //     // const i = photoAreaData.indexOf(targetPa);
  //     // const window = targetWindow.data.find((v: any) => v.id === `${CJ_DPOBJ_NAME.additionalWindow}:${i + 1}`);
  //     return window;
  //   }
  // }

}
